/**
 * Minesweeper Statistics Service
 *
 * Records game results via StatisticsManager, tracks per-difficulty
 * stats including best times, win rates, and streaks.
 *
 * Follows SudokuStatisticsService pattern.
 */

import {
  MinesweeperStatistics,
  MinesweeperGameResult,
  MinesweeperDifficultyStats,
  MinesweeperDifficulty,
} from '../types';
import { StatisticsManager } from '../../services/StatisticsManager';

export class MinesweeperStatisticsService {
  private statsManager: StatisticsManager;
  private readonly STATS_KEY = 'minesweeper_statistics';

  constructor(statsManager: StatisticsManager) {
    this.statsManager = statsManager;
  }

  private createEmptyStats(): MinesweeperStatistics {
    return {
      beginner: this.createEmptyDifficultyStats(),
      intermediate: this.createEmptyDifficultyStats(),
      expert: this.createEmptyDifficultyStats(),
      totalGames: 0,
      totalPlayTime: 0,
      currentStreak: 0,
      bestStreak: 0,
    };
  }

  private createEmptyDifficultyStats(): MinesweeperDifficultyStats {
    return {
      gamesPlayed: 0,
      gamesWon: 0,
      bestTime: null,
      averageTime: 0,
      winPercentage: 0,
    };
  }

  /** Get statistics for a user (or global if no userId). */
  getStatistics(userId?: string): MinesweeperStatistics {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      const data = localStorage.getItem(key);
      return data ? (JSON.parse(data) as MinesweeperStatistics) : this.createEmptyStats();
    } catch (error) {
      console.error('Failed to get minesweeper statistics:', error);
      return this.createEmptyStats();
    }
  }

  /** Record a completed game result. */
  recordGameResult(result: MinesweeperGameResult, userId?: string): boolean {
    try {
      const stats = this.getStatistics(userId);
      const diffStats = stats[result.difficulty];

      diffStats.gamesPlayed++;

      if (result.won) {
        diffStats.gamesWon++;

        // Update best time
        if (diffStats.bestTime === null || result.time < diffStats.bestTime) {
          diffStats.bestTime = result.time;
        }

        // Update average time (only for wins)
        const totalTime = diffStats.averageTime * (diffStats.gamesWon - 1) + result.time;
        diffStats.averageTime = Math.round(totalTime / diffStats.gamesWon);

        // Update streak
        stats.currentStreak++;
        if (stats.currentStreak > stats.bestStreak) {
          stats.bestStreak = stats.currentStreak;
        }
      } else {
        stats.currentStreak = 0;
      }

      diffStats.winPercentage =
        diffStats.gamesPlayed > 0
          ? (diffStats.gamesWon / diffStats.gamesPlayed) * 100
          : 0;

      stats.totalGames++;
      stats.totalPlayTime += result.time;

      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(stats));
      return true;
    } catch (error) {
      console.error('Failed to record minesweeper game result:', error);
      return false;
    }
  }

  /** Get statistics for a specific difficulty. */
  getDifficultyStatistics(
    difficulty: MinesweeperDifficulty,
    userId?: string
  ): MinesweeperDifficultyStats {
    const stats = this.getStatistics(userId);
    return stats[difficulty];
  }

  /** Reset all statistics. */
  resetStatistics(userId?: string): boolean {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(this.createEmptyStats()));
      return true;
    } catch (error) {
      console.error('Failed to reset minesweeper statistics:', error);
      return false;
    }
  }
}
